import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useState } from "react";

const useStyles = makeStyles(theme => ({
  descriptionText: {
    color: "#6e6e6e"
  },
  tokenContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  tokenLabel: {
    color: "black",
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  tokenInputContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px"
  },
  tokenField: {
    fontFamily: "monospace",
    "& .MuiInputBase-input": {
      fontFamily: "monospace",
      fontSize: "0.875rem",
      wordBreak: "break-all",
      padding: "8.5px 14px"
    },
    "& .MuiOutlinedInput-root": {
      padding: 0
    }
  },
  copyButton: {
    color: "black",
    borderColor: "#bdbdbd",
    textTransform: "none",
    whiteSpace: "nowrap",
    height: "40px",
    width: "90px"
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1)
  },
  successText: {
    color: theme.palette.success.main,
    marginTop: theme.spacing(1)
  },
  closeButton: {
    textTransform: "none",
    color: "white"
  }
}));

interface TokenDisplayPopupProps {
  open: boolean;
  token: string;
  onClose: () => void;
}

const TokenDisplayPopup: React.FC<TokenDisplayPopupProps> = ({
  open,
  token,
  onClose
}) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(token).then(
      () => {
        setCopied(true);
        setCopyError(false);
        setTimeout(() => setCopied(false), 3000);
      },
      () => {
        setCopyError(true);
        setCopied(false);
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="token-dialog-title"
    >
      <DialogTitle id="token-dialog-title">
        Access Grant Created Successfully
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          gutterBottom
          className={classes.descriptionText}
        >
          Your access grant has been created. Please copy the token below. This
          token will not be shown again.
        </Typography>
        <div className={classes.tokenContainer}>
          <Typography variant="body2" className={classes.tokenLabel}>
            Token
          </Typography>
          <div className={classes.tokenInputContainer}>
            <TextField
              fullWidth
              multiline
              value={token}
              variant="outlined"
              className={classes.tokenField}
              InputProps={{
                readOnly: true
              }}
              inputProps={{ "data-testid": "token-value" }}
            />
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              className={classes.copyButton}
              data-testid="copy-token-button"
            >
              Copy
            </Button>
          </div>
          {copyError && (
            <Typography variant="body2" className={classes.errorText}>
              Failed to copy to clipboard due to insufficient browser
              permissions. Please manually select and copy the token above.
            </Typography>
          )}
        </div>
        {copied && (
          <Typography variant="body2" className={classes.successText}>
            ✓ Token copied to clipboard
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          className={classes.closeButton}
          data-testid="close-token-popup-button"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokenDisplayPopup;
